import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { st, classes } from './Input.st.css.js';
import { InputContext } from './InputContext';
import { SIZES } from './Input.constants';
import { STATUS } from '../StatusIndicator/StatusIndicator.constants';
import Ticker from './Ticker';
import IconAffix from './IconAffix';
import Affix from './Affix';
import Group from './Group';
import InputSuffix, { getVisibleSuffixCount } from './InputSuffix';
import { WixStyleReactMaskingContext } from '../WixStyleReactMaskingProvider/context';
import { WixStyleReactDefaultsOverrideContext } from '../WixStyleReactDefaultsOverrideProvider';
import { StatusContext, getStatusFromContext, getAriaAttributesFromContext, } from '../FormField/StatusContext';
const clearButtonSizeMap = {
    [SIZES.tiny]: 'small',
    [SIZES.small]: 'small',
    [SIZES.medium]: 'medium',
    [SIZES.large]: 'medium',
};
class Input extends Component {
    constructor(props) {
        super(props);
        this.input = null;
        this._onCompositionChange = (isComposing) => {
            if (this.props.onCompositionChange) {
                this.props.onCompositionChange(isComposing);
            }
            this.isComposing = isComposing;
        };
        this._extractRef = (ref) => {
            const { inputRef } = this.props;
            this.input = ref;
            if (inputRef) {
                inputRef(ref);
            }
        };
        this._handleSuffixOnClear = (event) => {
            const { focusOnClearClick } = this.props;
            focusOnClearClick && this.focus();
            this.clear(event);
        };
        this._onFocus = (event) => {
            const { onFocus } = this.props;
            this._isMounted && this.setState({ focus: true });
            onFocus && onFocus(event);
            if (this.props.autoSelect) {
                // Set timeout is needed here since onFocus is called before react
                // gets the reference for the input (specifically when autoFocus
                // is on. So setTimeout ensures we have the ref.input needed in select)
                setTimeout(() => {
                    /**
                      here we trying to cover edge case with chrome forms autofill,
                      after user will trigger chrome form autofill, onFocus will be called for each input,
                      each input will cause this.select, select may(mostly all time) cause new onFocus,
                      which will cause new this.select, ..., we have recursion which will all time randomly cause
                      inputs to become focused.
                      To prevent this, we check, that current input node is equal to focused node.
                    */
                    if (document && document.activeElement === this.input) {
                        this.select();
                    }
                }, 0);
            }
        };
        this._onBlur = (event) => {
            this.setState({ focus: false });
            if (this.props.onBlur) {
                this.props.onBlur(event);
            }
        };
        this._onClick = (event) => {
            this.props.onInputClicked && this.props.onInputClicked(event);
        };
        this._onKeyDown = (event) => {
            if (this.isComposing) {
                return;
            }
            const { onKeyDown, onEnterPressed, onEscapePressed } = this.props;
            // On key event
            onKeyDown && onKeyDown(event);
            // Enter
            if (event.key === 'Enter' || event.keyCode === 13) {
                onEnterPressed && onEnterPressed(event);
            }
            // Escape
            if (event.key === 'Escape' || event.keyCode === 27) {
                onEscapePressed && onEscapePressed(event);
            }
        };
        this._isValidInput = (value) => {
            const { type } = this.props;
            if (type === 'number') {
                /*
                 * Limit our number input to contain only:
                 * - \d - digits
                 * - .  - a dot
                 * - ,  - a comma
                 * - \- - a hyphen minus
                 * - +  - a plus sign
                 */
                return /^[\d.,\-+]*$/.test(value);
            }
            return true;
        };
        this._onChange = (event) => {
            const { onChange } = this.props;
            if (this._isValidInput(event.target.value)) {
                onChange && onChange(event);
            }
        };
        this._onKeyPress = (event) => {
            if (!this._isValidInput(event.currentTarget.value + event.key)) {
                event.preventDefault();
            }
        };
        this._renderInput = (props) => {
            const { customInput, ref, ...rest } = props;
            const CustomInputComponent = customInput;
            const inputProps = {
                ...(CustomInputComponent
                    ? { ref, ...rest, 'data-hook': 'wsr-custom-input' }
                    : { ref, ...rest }),
            };
            return React.cloneElement(CustomInputComponent ? React.createElement(CustomInputComponent, null) : React.createElement("input", null), inputProps);
        };
        this._shouldShowStatusSuffix = ({ finalStatus, statusFromProp, }) => !this.props.hideStatusSuffix &&
            (!!statusFromProp || finalStatus === 'loading') &&
            finalStatus &&
            Object.values(STATUS).includes(finalStatus);
        /**
         * Sets focus on the input element
         * @param {FocusOptions} options
         */
        this.focus = (options = {}) => {
            this.input && this.input.focus(options);
        };
        /**
         * Removes focus on the input element
         */
        this.blur = () => {
            this.input && this.input.blur();
        };
        /**
         * Selects all text in the input element
         */
        this.select = () => {
            this.input && this.input.select();
        };
        /**
         * Sets the start and end positions of the current text selection in the input element
         */
        this.setSelectionRange = (start, end) => {
            this.input && this.input.setSelectionRange(start, end);
        };
        /**
         * Clears the input.
         * Fires onClear with the given event triggered on the clear button
         *
         * @param event delegated to the onClear call
         */
        this.clear = (event) => {
            const { onClear } = this.props;
            if (!this._isControlled && this.input) {
                this.input.value = '';
            }
            onClear && onClear(event);
        };
        this._isMounted = false;
        this.state = {
            focus: false,
        };
    }
    componentDidMount() {
        this._isMounted = true;
        const { autoFocus, value } = this.props;
        autoFocus && this._onFocus();
        /*
         * autoFocus doesn't automatically selects text like focus do.
         * Therefore we set the selection range, but in order to support prior implementation we set the start position as the end in order to place the cursor there.
         */
        if (autoFocus && !!value) {
            this.input?.setSelectionRange(String(value).length, String(value).length);
        }
    }
    componentWillUnmount() {
        this._isMounted = false;
    }
    get _isClearFeatureEnabled() {
        const { onClear, clearButton } = this.props;
        return !!onClear || !!clearButton;
    }
    get _isControlled() {
        const { value } = this.props;
        return value !== undefined;
    }
    render(props = {}) {
        const { input: inputPropsDefaults } = this.context;
        const { id, name, value, placeholder, menuArrow, defaultValue, tabIndex, autoFocus, onKeyUp, onPaste, disableEditing, readOnly, prefix, suffix, type, maxLength, textOverflow, disabled, status: statusProp, statusMessage, tooltipPlacement, autocomplete, min, max, step, required, customInput, pattern, size = inputPropsDefaults.size, clearButtonTooltipContent, clearButtonTooltipProps, } = this.props;
        const onIconClicked = (event) => {
            if (!disabled) {
                this.input && this.input.focus();
                this._onClick(event);
            }
        };
        // this doesn't work for uncontrolled, "value" refers only to controlled input
        const isClearButtonVisible = this._isClearFeatureEnabled && !!value && !disabled;
        // Aria Attributes
        const shouldRenderSuffix = ({ finalStatus, statusFromProp, }) => {
            const visibleSuffixCount = getVisibleSuffixCount({
                status: this._shouldShowStatusSuffix({
                    finalStatus,
                    statusFromProp,
                }),
                statusMessage,
                disabled,
                isClearButtonVisible,
                menuArrow,
                suffix,
            });
            return visibleSuffixCount > 0;
        };
        const ariaAttribute = {};
        const propsKeys = Object.keys(this.props);
        propsKeys
            .filter(key => key.startsWith('aria'))
            .map(key => (ariaAttribute['aria-' + key.substr(4).toLowerCase()] =
            this.props[key]));
        const { className, ...inputElementProps } = props;
        const inputElement = (React.createElement(WixStyleReactMaskingContext.Consumer, null, ({ maskingClassNames }) => (React.createElement(StatusContext.Consumer, null, statusContext => this._renderInput({
            min,
            max,
            step,
            'data-hook': 'wsr-input',
            'data-mask': !!maskingClassNames,
            style: { textOverflow },
            ref: this._extractRef,
            className: st(classes.input, {}, maskingClassNames),
            id,
            name,
            disabled,
            defaultValue,
            value,
            onChange: this._onChange,
            onKeyPress: this._onKeyPress,
            maxLength,
            onFocus: this._onFocus,
            onBlur: this._onBlur,
            onWheel: () => {
                // Although it's opposed to the native behavior, we decided to blur an input type="number" on wheel event in order to prevent change in value.
                if (type === 'number')
                    this.blur();
            },
            onKeyDown: this._onKeyDown,
            onPaste,
            placeholder,
            tabIndex,
            autoFocus,
            onClick: this._onClick,
            onKeyUp,
            readOnly: readOnly || disableEditing,
            type,
            required,
            autoComplete: autocomplete,
            onCompositionStart: () => this._onCompositionChange(true),
            onCompositionEnd: () => this._onCompositionChange(false),
            customInput,
            pattern,
            ...getAriaAttributesFromContext(statusContext),
            ...ariaAttribute,
            ...inputElementProps,
        })))));
        return (React.createElement("div", { className: classes.wrapper },
            prefix && (React.createElement(InputContext.Provider, { value: { ...this.props, size, inPrefix: true } }, prefix)),
            inputElement,
            React.createElement(InputContext.Provider, { value: { ...this.props, size, inSuffix: true } },
                React.createElement(StatusContext.Consumer, null, statusContext => {
                    const status = getStatusFromContext(statusContext, statusProp) ?? undefined;
                    return (shouldRenderSuffix({
                        finalStatus: status,
                        statusFromProp: statusProp,
                    }) && (React.createElement(InputSuffix, { status: this._shouldShowStatusSuffix({
                            finalStatus: status,
                            statusFromProp: statusProp,
                        })
                            ? status
                            : undefined, statusMessage: statusMessage, disabled: disabled, onIconClicked: onIconClicked, isClearButtonVisible: isClearButtonVisible, onClear: this._handleSuffixOnClear, clearButtonSize: clearButtonSizeMap[size ?? SIZES.medium], menuArrow: menuArrow, suffix: suffix, tooltipPlacement: tooltipPlacement, clearButtonTooltipContent: clearButtonTooltipContent, clearButtonTooltipProps: clearButtonTooltipProps })));
                }))));
    }
}
Input.displayName = 'Input';
Input.defaultProps = {
    focusOnClearClick: true,
    autoSelect: true,
    border: 'standard',
    textOverflow: 'clip',
    maxLength: 524288,
    clearButton: false,
    hideStatusSuffix: false,
};
Input.propTypes = {
    dataHook: PropTypes.string,
    className: PropTypes.string,
    id: PropTypes.string,
    ariaControls: PropTypes.string,
    ariaDescribedby: PropTypes.string,
    ariaLabel: PropTypes.string,
    autoFocus: PropTypes.bool,
    autocomplete: PropTypes.string,
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
    status: PropTypes.oneOf(['error', 'warning', 'loading']),
    statusMessage: PropTypes.node,
    hideStatusSuffix: PropTypes.bool,
    forceFocus: PropTypes.bool,
    forceHover: PropTypes.bool,
    maxLength: PropTypes.number,
    menuArrow: PropTypes.bool,
    clearButton: PropTypes.bool,
    name: PropTypes.string,
    border: PropTypes.oneOf(['standard', 'round', 'bottomLine', 'none']),
    noLeftBorderRadius: PropTypes.bool,
    noRightBorderRadius: PropTypes.bool,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    onCompositionChange: PropTypes.func,
    onEnterPressed: PropTypes.func,
    onEscapePressed: PropTypes.func,
    onFocus: PropTypes.func,
    onInputClicked: PropTypes.func,
    onKeyDown: PropTypes.func,
    onKeyUp: PropTypes.func,
    onPaste: PropTypes.func,
    placeholder: PropTypes.string,
    prefix: PropTypes.node,
    readOnly: PropTypes.bool,
    disableEditing: PropTypes.bool,
    size: PropTypes.oneOf(['tiny', 'small', 'medium', 'large']),
    suffix: PropTypes.node,
    tabIndex: PropTypes.number,
    textOverflow: PropTypes.string,
    tooltipPlacement: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    required: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    customInput: PropTypes.elementType
        ? PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.node,
            PropTypes.elementType,
        ])
        : PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
    pattern: PropTypes.string,
    focusOnClearClick: PropTypes.bool,
    clearButtonTooltipContent: PropTypes.node,
    clearButtonTooltipProps: PropTypes.node,
};
Input.contextType = WixStyleReactDefaultsOverrideContext;
Input.Ticker = Ticker;
Input.IconAffix = IconAffix;
Input.Affix = Affix;
Input.Group = Group;
Input.StatusError = STATUS.ERROR;
Input.StatusWarning = STATUS.WARNING;
Input.StatusLoading = STATUS.LOADING;
export default Input;
//# sourceMappingURL=Input.js.map